import { RigFracActivityMetric, RigFracGroupBy } from "constants/charts.enums";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import {
  rigFracCountyFieldName,
  rigFracSubBasinFieldName
} from "utils/map/rigFracValueMapping";

/**
 * Group-by dimensions for the Rig/Frac Activity chart (EVA-76). Shared by the
 * toolbar group-by selector and the legend title so the displayed label for a
 * given dimension is defined once.
 */
export const RIG_FRAC_GROUP_BY_OPTIONS: {
  label: string;
  value: RigFracGroupBy;
  // EVA-338. Offered on AWS only, to match the rig/frac layer filter there.
  awsOnly?: boolean;
}[] = [
  { label: "Operator", value: RigFracGroupBy.Operator },
  { label: "Basin", value: RigFracGroupBy.Basin },
  {
    get label() {
      return rigFracSubBasinFieldName("Sub-Basin");
    },
    value: RigFracGroupBy.SubBasin
  },
  { label: "Country", value: RigFracGroupBy.Country },
  { label: "State/Province", value: RigFracGroupBy.StateProvince },
  {
    get label() {
      return rigFracCountyFieldName("County");
    },
    value: RigFracGroupBy.County
  },
  { label: "Location Type", value: RigFracGroupBy.LocationType, awsOnly: true },
  { label: "Rig Trajectory", value: RigFracGroupBy.RigTrajectory }
];

/**
 * Dimensions that only apply to the rig-count metric (EVA-107). Frac crews have
 * no rig, so grouping frac activity by trajectory yields one flat "n/a" series.
 * The selector
 * offers these only while rig count is the active metric — the label lookup above
 * deliberately still resolves them, so an already-applied selection renders with
 * its real name rather than falling back to the raw enum value.
 */
export const RIG_COUNT_ONLY_GROUP_BY: readonly RigFracGroupBy[] = [
  RigFracGroupBy.RigTrajectory
];

// EVA-105. Wells have no location type, so permits would collapse to one "n/a" series.
const PERMITS_UNSUPPORTED_GROUP_BY: readonly RigFracGroupBy[] = [
  RigFracGroupBy.LocationType
];

/** Whether the group-by menu offers `groupBy` while `metric` drives the bars. */
export const isGroupByOfferedFor = (
  metric: RigFracActivityMetric,
  groupBy: RigFracGroupBy
): boolean =>
  (metric === RigFracActivityMetric.RigCount ||
    !RIG_COUNT_ONLY_GROUP_BY.includes(groupBy)) &&
  (metric !== RigFracActivityMetric.ApprovedPermits ||
    !PERMITS_UNSUPPORTED_GROUP_BY.includes(groupBy));

/** Display label for a rig/frac group-by dimension, defaulting to the request default. */
export const getRigFracGroupByLabel = (value: Maybe<RigFracGroupBy>): string => {
  const resolved = value ?? DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.groupBy;
  return RIG_FRAC_GROUP_BY_OPTIONS.find((o) => o.value === resolved)?.label ?? resolved;
};
